/* eslint-disable camelcase */

/**
 * Parse a viewBox string into an object with x, y, width, height.
 * Falls back to parsed_view_box if available.
 */
export const parseViewBox = ( parsed_view_box, viewBox ) => {
  if ( parsed_view_box ) return parsed_view_box;
  if ( !viewBox ) return null;
  const [vbX, vbY, vbWidth, vbHeight] = viewBox.split( ' ' ).map( Number );
  return {
    x: vbX || 0, y: vbY || 0, width: vbWidth || 1000, height: vbHeight || 1000,
  };
};

/**
 * Derive the cluster viewBox dimensions from parsed or raw viewBox data.
 */
export const getClusterViewBox = ( parsedVb, vbString ) => {
  if ( parsedVb ) return parsedVb;
  if ( !vbString ) return { width: 1000, height: 1000 };
  const [, , vbWidth, vbHeight] = vbString.split( ' ' ).map( Number );
  return { width: vbWidth || 1000, height: vbHeight || 1000 };
};

/**
 * Normalize zoom zones from an array to an object keyed by id/tid/index.
 */
export const normalizeZoomZones = ( rawZoomZones ) => {
  if ( !rawZoomZones ) return {};
  if ( !Array.isArray( rawZoomZones ) ) return rawZoomZones;
  return rawZoomZones.reduce( ( acc, zone, index ) => {
    const key = zone?.id || zone?.tid || index;
    acc[key] = zone;
    return acc;
  }, {} );
};

/**
 * Resolve the level SVG ID a zoom-zone click should switch the map to.
 * Prefers the zone's configured `default_level_svg_id`; otherwise falls
 * back to the community's initial level. Returns null if neither is set.
 */
export const pickZoomZoneTargetLevelSvgId = ( zoomZone, initialLevelSvgId ) => (
  zoomZone?.default_level_svg_id || initialLevelSvgId || null
);

/**
 * Determine the fallback level SVG ID from available levels.
 */
export const getFallbackLevelSvgId = ( svgLevels, initialLevelSvgId, birdsEyeLevelId ) => {
  if ( !svgLevels ) return null;
  if ( initialLevelSvgId && svgLevels[initialLevelSvgId] ) return initialLevelSvgId;
  if ( birdsEyeLevelId && svgLevels[birdsEyeLevelId] ) return birdsEyeLevelId;
  const levelIds = Object.keys( svgLevels );
  return levelIds.length > 0 ? levelIds[0] : null;
};

/**
 * Process changedUnits into a status updates map keyed by SVG ID.
 */
export const buildStatusUpdates = ( changedUnits, residenceStatusOptions ) => {
  const updates = {};
  changedUnits?.forEach( ( changedUnit ) => {
    const changedUnitSvgId = changedUnit?.data?.attributes?.field_svg_id;
    const changedUnitRelationships = changedUnit?.data?.relationships;
    const changedUnitStatusUuid = changedUnitRelationships?.field_status?.data?.id;

    if ( changedUnitSvgId ) {
      const matchingStatus = residenceStatusOptions?.find(
        ( status ) => status.id === changedUnitStatusUuid,
      );
      // API returns: abbreviation, considerUnoccupied (not fieldAbbreviation, fieldConsiderUnoccupied)
      updates[changedUnitSvgId] = {
        abbreviation: matchingStatus?.abbreviation || '',
        name: matchingStatus?.name,
        tid: matchingStatus?.drupalInternalId,
        uuid: changedUnitStatusUuid,
        consider_unoccupied: matchingStatus?.considerUnoccupied ? '1' : '0',
      };
    }
  } );
  return updates;
};

/**
 * Categorize units into active, inactive, and disabled arrays
 * based on focus, level of care, and available unit data.
 */
export const categorizeUnits = (
  unitsSvgData, mapActiveUnitData, svgFocusId, activeLevelOfCareUuid, statusUpdates,
) => {
  const activeUnitIds = new Set( Object.keys( mapActiveUnitData ) );
  const rawDisabledUnits = [];
  const rawActiveUnits = [];
  const rawInactiveUnits = [];

  const unitKeys = Object.keys( unitsSvgData );
  for ( let i = 0; i < unitKeys.length; i++ ) {
    const key = unitKeys[i];
    const sourceUnit = unitsSvgData[key];
    const unitSvgId = sourceUnit?.attributes?.id;
    const unitLoc = sourceUnit?.info?.level_of_care?.uuid;

    const isActive = svgFocusId
      ? unitSvgId === svgFocusId
      : activeUnitIds.has( unitSvgId );

    const matchesLevelOfCare = !activeLevelOfCareUuid || unitLoc === activeLevelOfCareUuid;

    const prevDisplayMode = sourceUnit.displayMode;
    const isFirstCategorization = sourceUnit.displayModeVersion === undefined;

    let newDisplayMode;
    if ( matchesLevelOfCare && isActive ) {
      newDisplayMode = 'active';
    } else if ( matchesLevelOfCare ) {
      newDisplayMode = 'inactive';
    } else {
      newDisplayMode = 'disabled';
    }

    const newVersion = ( isFirstCategorization || prevDisplayMode !== newDisplayMode )
      ? ( sourceUnit.displayModeVersion || 0 ) + 1
      : sourceUnit.displayModeVersion;

    const storedStatusUpdate = statusUpdates[unitSvgId];

    const unit = {
      ...sourceUnit,
      displayMode: newDisplayMode,
      displayModeVersion: newVersion,
      ...( storedStatusUpdate && {
        info: {
          ...sourceUnit.info,
          residence_status: storedStatusUpdate,
        },
      } ),
    };

    if ( newDisplayMode === 'active' ) {
      rawActiveUnits.push( unit );
    } else if ( newDisplayMode === 'inactive' ) {
      rawInactiveUnits.push( unit );
    } else {
      rawDisabledUnits.push( unit );
    }
  }

  return {
    activeUnits: rawActiveUnits,
    inactiveUnits: rawInactiveUnits,
    disabledUnits: rawDisabledUnits,
    allUnits: [...rawDisabledUnits, ...rawActiveUnits, ...rawInactiveUnits],
  };
};

/**
 * Calculate zoom scale factor for label clustering.
 * Higher zoom = smaller bounds (less clustering).
 */
export const getZoomScale = ( activeZoom, baseZoom ) => {
  const base = baseZoom || 18;
  const zoomDiff = base - ( activeZoom || base );
  return 1 + ( zoomDiff * 0.3 );
};

// Pick the LoC uuid with the lowest numeric weight (Drupal convention: lower
// weight = higher priority). Entries missing/non-numeric weight sort last.
export const pickPrimaryLevelOfCareUuid = ( levelOfCare ) => {
  if ( !Array.isArray( levelOfCare ) || levelOfCare.length === 0 ) return null;
  const ranked = levelOfCare
    .filter( ( loc ) => loc?.uuid )
    .map( ( loc ) => {
      const raw = parseInt( loc.weight, 10 );
      return { uuid: loc.uuid, weight: Number.isNaN( raw ) ? Infinity : raw };
    } )
    .sort( ( a, b ) => a.weight - b.weight );
  return ranked[0]?.uuid ?? null;
};
